import { ArrowForward, Code, Coffee, Palette, Security, Speed } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Fade,
  Grid,
  Slide,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import me from '../../constants/me';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'transparent',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  background: 'rgba(34, 40, 49, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(5),
  border: '1px solid rgba(0, 173, 181, 0.2)',
  position: 'relative',
  zIndex: 1,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    transition: 'width 0.8s ease-in-out',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'rgba(0, 173, 181, 0.1)',
    borderRadius: 2,
  },
  '&.animate::after': {
    width: 80,
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  maxWidth: 600,
  margin: `${theme.spacing(6)}px auto ${theme.spacing(8)}px auto`,
  fontStyle: 'italic',
  position: 'relative',
  lineHeight: 1.6,
  paddingTop: theme.spacing(2),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
  },
}));

const AboutCard = styled(Card)(({ theme }) => ({
  background: 'rgba(57, 62, 70, 0.9)',
  backdropFilter: 'blur(15px)',
  border: '1px solid rgba(0, 173, 181, 0.2)',
  borderRadius: theme.spacing(2.5),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 173, 181, 0.15)',
    border: '1px solid rgba(0, 173, 181, 0.4)',
    background: 'rgba(57, 62, 70, 0.95)',
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  fontSize: '1.5rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  flexShrink: 0,
  boxShadow: '0 8px 24px rgba(0, 173, 181, 0.25)',
  [theme.breakpoints.up('sm')]: {
    width: 64,
    height: 64,
    fontSize: '1.6rem',
  },
  [theme.breakpoints.up('md')]: {
    width: 70,
    height: 70,
    fontSize: '1.7rem',
  },
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: theme.palette.text.primary,
  lineHeight: 1.3,
  textAlign: 'center',
  fontFamily: 'Gilroy, sans-serif',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.2rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.3rem',
  },
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
  fontSize: '0.85rem',
  textAlign: 'center',
  opacity: 0.9,
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.9rem',
    lineHeight: 1.55,
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '0.95rem',
    lineHeight: 1.6,
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  padding: theme.spacing(4),
  background: 'rgba(0, 173, 181, 0.1)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(0, 173, 181, 0.2)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2),
    marginTop: theme.spacing(4),
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 1),
  },
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(1.5rem, 4vw, 3rem)',
  color: theme.palette.primary.main,
  lineHeight: 1,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: 'clamp(1.25rem, 5vw, 2rem)',
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1.2,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    letterSpacing: '0.25px',
    lineHeight: 1.3,
  },
}));

const AboutTextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  minHeight: 400,
  padding: theme.spacing(3),
  background: 'rgba(57, 62, 70, 0.95)',
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(0, 173, 181, 0.3)',
  [theme.breakpoints.between('sm', 'lg')]: {
    minHeight: 500,
    padding: theme.spacing(4),
  },
}));

const HighlightChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.primary.light}20)`,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}40`,
  margin: theme.spacing(0.5),
  fontWeight: 600,
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}30, ${theme.palette.primary.light}30)`,
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(1.5, 3),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  color: theme.palette.background.default,
  fontWeight: 600,
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 173, 181, 0.3)',
  },
}));

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  suffix?: string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <Box
      ref={(el: HTMLDivElement | null) => {
        if (el) {
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) {
                setIsVisible(true);
                observer.disconnect();
              }
            },
            { threshold: 0.5 }
          );
          observer.observe(el);
        }
      }}
    >
      <StatNumber>{count}{suffix}</StatNumber>
    </Box>
  );
};

const AboutSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animateTitle, setAnimateTitle] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Delay the title animation slightly for better effect
          setTimeout(() => setAnimateTitle(true), 300);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('about');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Code />,
      title: 'Clean Code',
      description: 'Writing maintainable, scalable code following best practices.',
    },
    {
      icon: <Palette />,
      title: 'Modern Design',
      description: 'Creating beautiful, intuitive user interfaces with attention to detail.',
    },
    {
      icon: <Speed />,
      title: 'Performance',
      description: 'Optimizing applications for speed and excellent user experience.',
    },
    {
      icon: <Security />,
      title: 'Security',
      description: 'Implementing robust security measures to protect applications.',
    },
    {
      icon: <Coffee />,
      title: 'Innovation',
      description: 'Exploring cutting-edge technologies and creative solutions.',
    },
    {
      icon: <ArrowForward />,
      title: 'Collaboration',
      description: 'Working effectively in teams and mentoring junior developers.',
    },
  ];

  const stats = [
    { number: 5, suffix: '+', label: 'Years of Full Stack Experience' },
    { number: 7, suffix: '+', label: 'Production Projects Delivered' },
    { number: 10, suffix: '+', label: 'Technologies & Tools Used' },
    { number: '☕', suffix: '', label: 'Driven by Curiosity, Fueled by Coffee' }
  ];

  const highlights = [
    'Web Application Development',
    'Associate System Architecture',
    'Micro Frontend Architecture',
    'Microservices Architecture',
    'Cloud-Native Solutions',
    'Enterprise Authentication',
    'Serverless Technologies',
    'Team Player',
    'Technical Mentorship'
  ];

  return (
    <SectionContainer id="about">
      <Container maxWidth="lg">
        <ContentWrapper>
          <Fade in={isVisible} timeout={800}>
            <Box>
              <SectionTitle variant="h2" className={animateTitle ? 'animate' : ''}>
                About Me
              </SectionTitle>
              <SectionSubtitle variant="subtitle1" sx={{whiteSpace: 'nowrap', textAlign: 'center',margin:'0 auto'}}>
                "Passionate about creating digital solutions that make a difference"
              </SectionSubtitle>
            </Box>
          </Fade>
          
          <Box mt={2} mb={2}/>
          <Grid container spacing={{ xs: 4, sm: 5, md: 6 }} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Slide direction="right" in={isVisible} timeout={1000}>
                <AboutTextContainer>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: 'clamp(1rem, 2vw, 1.1rem)', sm: '1.15rem', md: '1.1rem' },
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      mb: 3,
                    }}
                  >
                    {me.about}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: 'clamp(1rem, 2vw, 1.1rem)', sm: '1.15rem', md: '1.1rem' },
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      mb: 3,
                    }}
                  >
                    I'm constantly learning new technologies and improving my skills to stay current 
                    with the rapidly evolving tech landscape. My goal is to create solutions that 
                    not only meet technical requirements but also provide exceptional user experiences.
                  </Typography>
                  
                  <Box sx={{ mb: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.1rem' },
                      }}
                    >
                      Key Expertise
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {highlights.map((highlight, index) => (
                        <Fade key={index} in={isVisible} timeout={1400 + index * 100}>
                          <HighlightChip
                            label={highlight}
                            size="small"
                            variant="outlined"
                          />
                        </Fade>
                      ))}
                    </Box>
                  </Box>

                  <Fade in={isVisible} timeout={1800}>
                    <CTAButton
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => {
                        const element = document.getElementById('skills');
                        element?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      My Skills
                    </CTAButton>
                  </Fade>
                </AboutTextContainer>
              </Slide>
            </Grid>

                          <Grid item xs={12} md={6}>
                <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} sm={4} md={6} key={index} sx={{
                      [theme.breakpoints.up(412)]: {
                        flexBasis: '50%',
                        maxWidth: '50%'
                      }
                    }}>
                      <Fade in={isVisible} timeout={1200 + index * 200}>
                        <AboutCard>
                          <Box sx={{
                            p: { xs: 2, sm: 2.5, md: 3 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            height: '100%',
                            minHeight: { xs: '140px', sm: '180px', md: '200px' },
                            [theme.breakpoints.up(412)]: {
                              minHeight: '160px'
                            }
                          }}>
                            <FeatureIcon>
                              {feature.icon}
                            </FeatureIcon>
                            <FeatureTitle variant="h6" sx={{ mt: 1.5, mb: 1 }}>
                              {feature.title}
                            </FeatureTitle>
                            <FeatureDescription variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                              {feature.description}
                            </FeatureDescription>
                          </Box>
                        </AboutCard>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
          </Grid>

          <Slide direction="up" in={isVisible} timeout={1600}>
            <StatsContainer>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {stats.map((stat, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <StatItem>
                      {stat.number === '☕' 
                      ? <StatNumber sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: { xs: '2.5rem', sm: '3rem' },
                          mb: 1
                        }}>
                          <Coffee sx={{ 
                            fontSize: { xs: '2rem', sm: '2.5rem' }, 
                            color: 'primary.main' 
                          }} />
                        </StatNumber>
                      : <AnimatedCounter end={Number(stat.number)} suffix={stat.suffix} />}
                      <StatLabel>{stat.label}</StatLabel>
                    </StatItem>
                  </Grid>
                ))}
              </Grid>
            </StatsContainer>
          </Slide>
        </ContentWrapper>
      </Container>
    </SectionContainer>
  );
};

export default AboutSection; 